import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';

import { FadeIn } from './fade-in';

interface Feature {
  number: string;
  title: string;
  description: string;
  image: string;
}

interface ProductKeyFeaturesSectionProps {
  title: string;
  features: Feature[];
}

/**
 * Individual feature card with scroll-driven opacity and Y-offset.
 * Each card fades in and slides up as it enters the viewport,
 * using GPU-composited transform + opacity for zero layout cost.
 */
const FeatureCard = ({
  feature,
  index,
}: {
  feature: Feature;
  index: number;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  /* Scroll-linked vertical parallax: card content rises 24px as it scrolls into view */
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'start 0.4'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [24, 0]);

  return (
    <div
      ref={cardRef}
      className="bg-neutral-1 px-4 md:px-8 lg:px-20 top-[150px] sticky md:top-[298px]"
      style={{ zIndex: index + 1 }}
    >
      <div className="h-px bg-black/10 w-full mb-6 md:mb-0" />

      <motion.div
        className="flex flex-col lg:flex-row lg:pt-[24px] lg:pb-[60px] md:justify-between gap-6 md:gap-8 will-change-transform"
        style={{ y }}
      >
        <div className="flex flex-col gap-6 lg:gap-0 text-black lg:flex-1">
          <div className="flex flex-col gap-3 lg:gap-[24px] lg:flex-row">
            <p className="font-mono text-sm leading-normal tracking-[0.14px] uppercase lg:w-[105px] lg:shrink-0">
              {feature.number}
            </p>
            <p className="font-medium text-xl leading-[1.4] lg:w-[233px] lg:shrink-0">
              {feature.title}
            </p>
            <p className="text-base font-normal leading-[1.5] tracking-[0.16px] lg:max-w-[300px]">
              {feature.description}
            </p>
          </div>
        </div>

        <div className="w-full aspect-[343/292] mb-[60px] lg:mb-0 md:aspect-auto lg:h-[292px] lg:w-[512px] lg:shrink-0">
          <img
            src={feature.image}
            alt={feature.title}
            className="w-full h-full object-cover"
          />
        </div>
      </motion.div>
    </div>
  );
};

export const ProductKeyFeaturesSection = ({
  title,
  features,
}: ProductKeyFeaturesSectionProps) => {
  return (
    <section className="relative w-full bg-neutral-1 pb-10 lg:pb-[120px] min-h-[800px]">
      <div className="sticky top-[40px] lg:top-[80px] z-20 bg-neutral-1 px-4 md:px-8 lg:px-20 pt-10 lg:pt-[120px] pb-5 lg:pb-10">
        <FadeIn>
          <p className="text-2xl md:text-[40px] font-medium text-black">
            {title}
          </p>
        </FadeIn>
      </div>
      <div className="relative">
        {features.map((feature, index) => (
          <FeatureCard key={index} feature={feature} index={index} />
        ))}
      </div>
    </section>
  );
};
